import { MoreOutlined } from "@ant-design/icons"
import { Breadcrumb, Button, Dropdown } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import { ActionTooltip } from "../../../../components/ActionTooltip.tsx"
import { RoutesPaths } from "../../../../constants/routes.ts"
import type { ListStatus } from "../../helpers/automation-list"
import type { BuilderAction } from "../../types/builder-action"
import { StatusBadge } from "./StatusBadge"

interface BuilderHeaderProps {
  title: string
  subtitle?: string
  status: ListStatus
  /** False while the record or options are loading or failed: no actions yet. */
  ready: boolean
  canDuplicate: boolean
  busy: BuilderAction | null
  onSave: () => void
  onActivate: () => void
  onPause: () => void
  onDuplicate: () => void
  /** Brings the live review into view; the button only shows below xl, where it stacks. */
  onPreview: () => void
}

// Phones get the 44px touch target the design asks of primary controls.
const TOUCH = "max-sm:h-11"

export function BuilderHeader({
  title,
  subtitle,
  status,
  ready,
  canDuplicate,
  busy,
  onSave,
  onActivate,
  onPause,
  onDuplicate,
  onPreview
}: BuilderHeaderProps) {
  const { t } = useTranslation()
  const active = status === "active"
  // One request at a time: the running action shows its spinner, the rest wait.
  const pending = (action: BuilderAction) => ({
    loading: busy === action,
    disabled: busy !== null && busy !== action
  })

  return (
    <div className={"flex flex-col gap-2"}>
      <Breadcrumb
        items={[
          { title: <Link to={RoutesPaths.settings.automation}>{t("settingsAutomation.builder.header.breadcrumb")}</Link> },
          { title }
        ]}
      />
      <div className={"flex flex-wrap items-start justify-between gap-3"}>
        <div className={"flex min-w-0 flex-col gap-1"}>
          <div className={"flex min-w-0 flex-wrap items-center gap-3"}>
            {/* The global h1-h3 rule centres headings. */}
            <h2 className={"m-0 min-w-0 break-words text-start text-xl font-semibold"}>{title}</h2>
            <span data-testid={"automation-status"}>
              <StatusBadge status={status} />
            </span>
          </div>
          {subtitle && <p className={"m-0 text-sm text-gray-500"}>{subtitle}</p>}
        </div>
        {ready && (
          <div data-testid={"automation-header-actions"} className={"flex flex-wrap items-center gap-2"}>
            <Button key={"preview"} className={`xl:hidden ${TOUCH}`} onClick={onPreview}>
              {t("settingsAutomation.builder.header.preview")}
            </Button>
            {active ? (
              <>
                <Button key={"pause"} className={TOUCH} {...pending("pause")} onClick={onPause}>
                  {t("settingsAutomation.builder.header.pause")}
                </Button>
                <Button key={"save-changes"} type={"primary"} className={TOUCH} {...pending("save")} onClick={onSave}>
                  {t("settingsAutomation.builder.header.saveChanges")}
                </Button>
              </>
            ) : (
              <>
                <Button key={"save-draft"} className={TOUCH} {...pending("save")} onClick={onSave}>
                  {t("settingsAutomation.builder.header.saveDraft")}
                </Button>
                <Button
                  key={"activate"}
                  type={"primary"}
                  className={TOUCH}
                  {...pending("activate")}
                  onClick={onActivate}
                >
                  {t("settingsAutomation.builder.header.reviewAndActivate")}
                </Button>
              </>
            )}
            {canDuplicate && (
              // Dropdown must own the trigger; both clone their child.
              <ActionTooltip title={t("settingsAutomation.builder.header.moreActions")}>
                <Dropdown
                  trigger={["click"]}
                  placement={"bottomRight"}
                  menu={{
                    items: [
                      {
                        key: "duplicate",
                        label: t("settingsAutomation.builder.header.duplicate"),
                        disabled: busy !== null,
                        onClick: onDuplicate
                      }
                    ]
                  }}
                >
                  <Button
                    aria-label={t("settingsAutomation.builder.header.moreActions")}
                    icon={<MoreOutlined aria-hidden={"true"} />}
                    loading={busy === "duplicate"}
                    className={"max-sm:h-11 max-sm:w-11"}
                  />
                </Dropdown>
              </ActionTooltip>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
